import type { Order as GQLItem } from '../../../generated/api.graphql';
import { Account } from '../Account';
import { Event } from '../Event';
import { Customer } from '../Customer';
import { take } from '../../../util';
import { OrderState } from './OrderState';
import { OrderOrigin } from './OrderOrigin';

/**
 * The schema's `Order` type. `OrderDetails` is a separate wire type and gets its own module,
 * `OrderDetail` — see the "One type per file" rule in `doc/claude/api-layer.md`.
 *
 * `refundStatus` is on this wire type and deliberately unmapped: it belongs to the detail read, and
 * `OrderDetail` maps it there.
 *
 * Two notes that apply to every mapper in this directory, since they all follow this shape:
 *
 * - **Nullable scalars arrive as `null`, not `undefined`.** `take` (`util/takePayload.ts`) skips only
 *   `undefined`, so the `?: string` types here under-describe what the mappers produce. The contract
 *   this module offers is that the two mean the same absence; a caller that needs to tell them apart
 *   should widen the domain type rather than have the mapper guess.
 * - **`take` does not typecheck its key list**, so a misspelled or forgotten key yields `undefined`
 *   with no compile error. Hence the whole-object `toEqual` opening each spec here.
 */
export type Order = {
  id: number;
  createdAt?: string;
  updatedAt?: string;
  state: OrderState;
  fingerprint?: string;
  transactionId?: string;
  total: number;
  currency: string;
  origin: OrderOrigin;
  scannedCount: number;
  ticketCount: number;
  account?: Account.Account;
  event?: Event.Event;
  customer?: Customer.Customer;
  paymentMethod?: string;
  cardBrand?: string;
  lastFourDigits?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
};

export const fromGql = (item: GQLItem): Order => ({
  ...take<Order>(item, 'id', 'createdAt', 'updatedAt', 'state', 'fingerprint', 'transactionId', 'total', 'currency', 'origin', 'scannedCount', 'ticketCount', 'paymentMethod', 'cardBrand', 'lastFourDigits', 'firstName', 'lastName', 'email', 'phoneNumber'),
  account: item.account ? Account.fromGql(item.account) : undefined,
  event: item.event ? Event.fromGql(item.event) : undefined,
  customer: item.customer ? Customer.fromGql(item.customer) : undefined,
});
